import React, { useEffect, useState } from "react";
import { fmtIST } from "../lib/time";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";

export default function MaterialHistory() {
  const { id } = useParams();
  const [events, setEvents] = useState([]);
  const [material, setMaterial] = useState(null);
  const [grnPreview, setGrnPreview] = useState(null);   // {loading, data}
  const [issuePreview, setIssuePreview] = useState(null); // event row clone
  const [allMaterials, setAllMaterials] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get(`/inventory/by-material/${id}`),
      api.get("/materials"),
    ]).then(([h, m]) => {
      setEvents(h.data);
      setMaterial(m.data.find((x) => x.id === id));
      setAllMaterials(m.data || []);
    });
  }, [id]);

  const openGrn = async (e) => {
    if (!e.grn_id) return;
    setGrnPreview({ loading: true, data: null });
    try {
      const { data } = await api.get(`/grns/${e.grn_id}`);
      setGrnPreview({ loading: false, data });
    } catch (err) {
      setGrnPreview({ loading: false, data: null, error: "Could not load GRN" });
    }
  };

  return (
    <div data-testid="material-history-page">
      <PageHeader
        title={material ? `${material.code} — ${material.name}` : "Material History"}
        subtitle="Inward + outward movement with running balance"
        right={<button onClick={() => nav(-1)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm flex items-center gap-2"><ArrowLeft size={14}/> Back</button>}
      />
      <div className="p-8">
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Reference</th>
                <th className="text-left px-4 py-2">Lot</th>
                <th className="text-right px-4 py-2">Qty</th>
                <th className="text-right px-4 py-2">Balance</th>
                <th className="text-left px-4 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-4 py-2 font-mono text-xs">{fmtIST(e.at)}</td>
                  <td className="px-4 py-2 text-[10px] uppercase tracking-widest font-bold" style={{ color: e.type === "in" ? "#2E7D32" : "#C62828" }}>{e.type === "in" ? "INWARD" : "OUTWARD"}</td>
                  <td className="px-4 py-2 font-mono">
                    {e.type === "in" ? (
                      e.grn_id ? (
                        <button
                          onClick={() => openGrn(e)}
                          className="text-[#1565C0] hover:underline font-bold"
                          data-testid={`mh-grn-link-${e.ref}`}
                        >{e.ref}</button>
                      ) : <span className="text-[#0D2B5E]/60">{e.ref}</span>
                    ) : (
                      <button
                        onClick={() => setIssuePreview(e)}
                        className="text-[#1565C0] hover:underline font-bold"
                        data-testid={`mh-issue-link-${e.ref}`}
                      >{e.ref}</button>
                    )}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{e.lot_no || "—"}</td>
                  <td className={`px-4 py-2 font-mono text-right ${e.type === "in" ? "text-[#2E7D32]" : "text-[#C62828]"}`}>{e.type === "in" ? "+" : "−"}{e.qty}</td>
                  <td className="px-4 py-2 font-mono text-right font-bold">{e.balance}</td>
                  <td className="px-4 py-2 text-xs">{e.note || "—"}</td>
                </tr>
              ))}
              {events.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-[#0D2B5E]/50">No movement.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {grnPreview && (
        <PreviewDialog title={grnPreview.data ? `GRN ${grnPreview.data.number}` : "GRN Preview"} onClose={() => setGrnPreview(null)} testId="mh-grn-preview">
          {grnPreview.loading && <div className="text-sm text-[#0D2B5E]/60">Loading…</div>}
          {grnPreview.error && <div className="text-sm text-[#C62828]">{grnPreview.error}</div>}
          {grnPreview.data && <GrnPreviewBody grn={grnPreview.data} materials={allMaterials}/>}
        </PreviewDialog>
      )}

      {issuePreview && (
        <PreviewDialog title={`Issue ${issuePreview.ref}`} onClose={() => setIssuePreview(null)} testId="mh-issue-preview">
          <IssuePreviewBody issue={issuePreview}/>
        </PreviewDialog>
      )}
    </div>
  );
}

function PreviewDialog({ title, onClose, children, testId }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid={testId}>
      <div className="bg-white rounded-sm w-[640px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#EEEEEE] sticky top-0 bg-white">
          <div className="text-sm font-bold uppercase tracking-widest text-[#0D2B5E]">{title}</div>
          <button onClick={onClose} className="text-[#0D2B5E]/60 hover:text-[#0D2B5E]" data-testid={`${testId}-close`}><X size={16}/></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function GrnPreviewBody({ grn, materials = [] }) {
  const matMap = React.useMemo(() => Object.fromEntries((materials || []).map((m) => [m.id, m])), [materials]);
  const lookupClass = (s) => {
    if (s === "stock_in" || s === "deviation_accepted" || s === "conditional") return "bg-[#2E7D32]/10 text-[#2E7D32]";
    if (s === "pending_quality") return "bg-[#1565C0]/10 text-[#1565C0]";
    if (s === "pending_deviation" || s === "rejected_return") return "bg-[#C62828]/10 text-[#C62828]";
    return "bg-[#0D2B5E]/10 text-[#0D2B5E]";
  };
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3 text-xs bg-[#F5F5F5] p-3 rounded-sm">
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Number</div><div className="font-mono font-bold text-[#1565C0]">{grn.number}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Status</div><span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${lookupClass(grn.status)}`}>{grn.status}</span></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Gate Entry</div><div className="font-mono">{grn.gate_entry_number || "—"}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">PO</div><div className="font-mono">{grn.po_number || "—"}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Invoice</div><div className="font-mono">{grn.invoice_no || "—"}</div></div>
        {/* Feb-2026 — requested: surface invoice date in Stock-movement GRN preview. */}
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Invoice Date</div><div className="font-mono">{grn.invoice_date || "—"}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Created</div><div className="font-mono text-[11px]">{fmtIST(grn.created_at)}</div></div>
      </div>
      <table className="w-full text-xs border border-[#EEEEEE]">
        <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
          <tr><th className="text-left px-2 py-1">Material</th><th className="text-right px-2 py-1">Received</th><th className="text-right px-2 py-1">Boxes</th><th className="text-right px-2 py-1">Rate</th><th className="text-right px-2 py-1">Value</th></tr>
        </thead>
        <tbody>
          {(grn.items || []).map((it, i) => {
            const m = matMap[it.material_id];
            // Feb-2026 — show readable material code/name instead of raw UUID.
            const label = m ? `${m.code} — ${m.name}` : it.material_id;
            return (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
              <td className="px-2 py-1 font-mono">{label}</td>
              <td className="px-2 py-1 font-mono text-right">{it.received_qty}</td>
              <td className="px-2 py-1 font-mono text-right">{it.accepted_packing_qty}</td>
              <td className="px-2 py-1 font-mono text-right">₹{(it.rate || 0).toFixed(2)}</td>
              <td className="px-2 py-1 font-mono text-right">₹{((it.received_qty || 0) * (it.rate || 0)).toFixed(2)}</td>
            </tr>
          );})}
        </tbody>
      </table>
      {grn.remarks && <div className="text-xs"><strong>Remarks:</strong> {grn.remarks}</div>}
    </div>
  );
}

function IssuePreviewBody({ issue }) {
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3 text-xs bg-[#F5F5F5] p-3 rounded-sm">
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Issue No.</div><div className="font-mono font-bold text-[#1565C0]">{issue.ref}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">When</div><div className="font-mono text-[11px]">{fmtIST(issue.at)}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Material</div><div className="font-mono">{issue.material_code} {issue.material_name && `· ${issue.material_name}`}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Qty Issued</div><div className="font-mono font-bold text-base text-[#C62828]">−{issue.qty}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">From GRN</div><div className="font-mono">{issue.grn_number || "—"}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Lot · Box</div><div className="font-mono">{issue.lot_no} {issue.box_no != null ? `· #${issue.box_no}` : ""}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Issued By</div><div>{issue.issued_by_name || "—"}</div></div>
        <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Production / Work Order</div><div className="font-mono">{issue.production_order || "—"}</div></div>
        {issue.material_request_number && (
          <div><div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">MR</div><div className="font-mono text-[#1565C0]">{issue.material_request_number}</div></div>
        )}
        {issue.ad_hoc_reason && (
          <div className="col-span-2"><div className="text-[10px] tracking-widest uppercase text-[#E65100] font-bold">Ad-Hoc Reason</div><div className="italic">{issue.ad_hoc_reason}</div></div>
        )}
        {issue.fifo_override_reason && (
          <div className="col-span-2"><div className="text-[10px] tracking-widest uppercase text-[#E65100] font-bold">FIFO Override</div><div className="italic">{issue.fifo_override_reason}</div></div>
        )}
      </div>
    </div>
  );
}
